import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { fetchCountriesList } from "../services/geo/countries";
import { fetchRegionsList } from "../services/geo/regions";
import { fetchCitiesList } from "../services/geo/cities";
import { pageSize } from "../services/settings";

export const useCountriesQuery = (
  search: string = "",
  page: number = 1,
  limit: number = 10,
  ordering?: string[]
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["countries", search, page, limit, ordering],
    queryFn: () =>
      fetchCountriesList({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        ordering,
      }),
    retry: 0,
  });
};

export const useCountriesInfiniteQuery = (
  search: string = "",
  limit: number = pageSize
) => {
  return useInfiniteQuery({
    queryKey: ["countries-infinite", search, limit],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchCountriesList({
        search: search || undefined,
        limit: pageParam.limit,
        offset: pageParam.offset,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useRegionsQuery = (
  search: string = "",
  page: number = 1,
  limit: number = 10,
  ordering?: string[],
  countryId?: string
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["regions", search, page, limit, ordering],
    queryFn: () =>
      fetchRegionsList({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        ordering,
        countryId,
      }),
    retry: 0,
  });
};

export const useCitiesQuery = (
  search: string = "",
  page: number = 1,
  limit: number = 10,
  ordering?: string[],
  countryId?: string[]
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["cities", search, page, limit, ordering],
    queryFn: () =>
      fetchCitiesList({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        ordering,
        country_id: countryId,
      }),
    retry: 0,
  });
};
